import { useState, useEffect, useRef } from "react";
import ChartTableComponent from "../components/charts/ChartTableComponent";
import { apiCall } from "@/api/apiCall";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import { useQueue } from "@/context/QueueContext";
import { processParams } from "../utils/paramsHelper";
import { useNavigate } from "react-router-dom";

const LineBars = ({
  year = "",
  month = "",
  monthNumber = "",
  contract = "",
  page = null,
  isRefresh = false
}) => {
  const { enqueue } = useQueue();
const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);

  const [chartData, setChartData] = useState({
    months: [],
    processedDatasets: [],
    processedBarDatasets: [],
    yearRanges: [],
    secondTableData: null,
  });

  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // ========== STEP 1: Build API parameters ==========
      

      const apiParams = processParams(page?.params || {}, { year, month, contract });

      const response = await enqueue(() =>
        apiCall("GET", "/boardpack/enforcement-monthly-trend", apiParams,navigate,false,false,isRefresh)
      );

      if (!response?.success) {
        throw new Error(response?.message || "Failed to fetch data");
      }

      const apiData = response.data;
      const upperTableData = response.upper_table;

      // ========== STEP 2: Extract year-month combinations ==========
      const allMonths = new Set();
      const yearMonthMap = {};

      const extractMonths = (dataObj) => {
        if (!dataObj) return;

        Object.values(dataObj).forEach((metricData) => {
          // Check for nested structure (upper_table with emp/dep)
          if (metricData?.emp || metricData?.dep) {
            [metricData.emp, metricData.dep].forEach((subData) => {
              if (subData) {
                Object.keys(subData).forEach((yr) => {
                  Object.keys(subData[yr] || {}).forEach((mn) => {
                    allMonths.add(`${yr}-${mn}`);
                    if (!yearMonthMap[yr]) yearMonthMap[yr] = new Set();
                    yearMonthMap[yr].add(mn);
                  });
                });
              }
            });
          } else {
            // Normal structure
            Object.keys(metricData || {}).forEach((yr) => {
              Object.keys(metricData[yr] || {}).forEach((mn) => {
                allMonths.add(`${yr}-${mn}`);
                if (!yearMonthMap[yr]) yearMonthMap[yr] = new Set();
                yearMonthMap[yr].add(mn);
              });
            });
          }
        });
      };

      extractMonths(apiData);
      extractMonths(upperTableData);

      if (allMonths.size === 0) {
        throw new Error("No data available");
      }

      // Sort months chronologically
      const monthOrder = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ];

      const sortedMonths = Array.from(allMonths).sort((a, b) => {
        const [yearA, monthA] = a.split("-");
        const [yearB, monthB] = b.split("-");
        if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);
        return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
      });

      const monthLabels = sortedMonths.map((item) => item.split("-")[1]);

      // Build year ranges
      const yearRanges = Object.keys(yearMonthMap)
        .sort()
        .map((yr) => ({
          year: yr,
          span: yearMonthMap[yr].size,
        }));

      // ========== STEP 3: Value parser ==========
      const parseValue = (rawValue) => {
        if (
          rawValue === null ||
          rawValue === undefined ||
          rawValue === "" ||
          rawValue === "-"
        ) {
          return 0;
        }

        if (typeof rawValue === "string") {
          const cleaned = rawValue.replace("%", "").trim();
          const num = parseFloat(cleaned);
          return isNaN(num) ? null : num;
        }

        const num = Number(rawValue);
        return isNaN(num) ? null : num;
      };

      // ========== STEP 4: Process datasets ==========
      const processedDatasets = (page?.datasets || []).map((datasetConfig) => {
        let values = [];

        if (page?.showSecondTable === "abc") {
          // upper_table source
          const metricData = upperTableData?.[datasetConfig.metric];

          if (!metricData || !metricData[datasetConfig.subType]) {
            console.warn(
              `Upper table "${datasetConfig.metric}.${datasetConfig.subType}" not found`
            );
            values = sortedMonths.map(() => null);
          } else {
            values = sortedMonths.map((yearMonth) => {
              const [yr, mn] = yearMonth.split("-");
              const rawValue = metricData[datasetConfig.subType]?.[yr]?.[mn];
              return parseValue(rawValue);
            });
          }
        } else {
          // Normal data source
          let actualMetric = datasetConfig.label;

          const labelMappings = {
            "EO/EOD's Emp": "CNS",
            "EO/EOD's Dep": "CNS",
            "EO/EOD'S": "CNS",
            "EO/EODS": "CNS",
            "Clamp Driver Emp": "CLAMP",
            "Clamp Driver Dep": "CLAMP",
            "TowDriver Emp": "TOW",
            "TowDriver Dep": "TOW",
            CLAMP: "CLAMP",
            TOW: "TOW",
            CNS: "CNS",
          };

          const mappedMetric = labelMappings[datasetConfig.label];
          if (mappedMetric) actualMetric = mappedMetric;

          const metricData = apiData[actualMetric];

          if (!metricData) {
            console.warn(
              `Metric "${actualMetric}" (from label "${datasetConfig.label}") not found in data`
            );
            values = sortedMonths.map(() => null);
          } else {
            values = sortedMonths.map((yearMonth) => {
              const [yr, mn] = yearMonth.split("-");
              const rawValue = metricData?.[yr]?.[mn];
              return parseValue(rawValue);
            });
          }
        }

        return {
          label: datasetConfig.label,
          data: values,
          color: datasetConfig.color,
          borderDash: datasetConfig.borderDash || [],
          order: datasetConfig.order || 0,
        };
      });

      // Sort by order
      processedDatasets.sort((a, b) => (a.order || 0) - (b.order || 0));


      // ========== STEP 5: Process bar datasets ==========
      // FIX: bar data must be index-aligned with sortedMonths
      let processedBarDatasets = [];

      if (page?.barDatasets && page.barDatasets.length > 0) {

        if (page?.showSecondTable) {
          // ── line_bars_table (pages 11, 12, 13) ──
          // Floating bar [min, max] between the two line datasets
          const lineDatasets = processedDatasets.filter(
            (ds) => ds.data && ds.data.some((val) => val !== null && val !== 0)
          );

          if (lineDatasets.length >= 2) {
            const firstDataset = lineDatasets[0];
            const secondDataset = lineDatasets[1];

            // data is already sortedMonths-length → alignment guaranteed ✓
            const floatingBarData = firstDataset.data.map((val1, i) => {
              const val2 = secondDataset.data[i];
              if (val1 === null || val2 === null) return null;
              if (val1 === 0 && val2 === 0) return null;
              return [Math.min(val1, val2), Math.max(val1, val2)];
            });

            processedBarDatasets = page.barDatasets.map((barConfig) => ({
              label: barConfig.label || "",
              data: floatingBarData, // sortedMonths-aligned ✓
              color: barConfig.color,
              order: barConfig.order || 0,
              hideInTable: barConfig.hideInTable !== false,
            }));
          }
        } else {
          // ── line_bars (pages 6, 7, 8, 9, 10) ──
          // Background bar = max value across all line datasets per month
          const allLineDatasets = processedDatasets.filter(
            (ds) => ds.data && ds.data.some((val) => val !== null && val !== 0)
          );

          if (allLineDatasets.length > 0) {
            // Build bar data with same length as sortedMonths → alignment guaranteed ✓
            const backgroundBarData = sortedMonths.map((_, i) => {
              const vals = allLineDatasets
                .map((ds) => ds.data[i])
                .filter((v) => v !== null && v !== 0);
              return vals.length > 0 ? Math.max(...vals) : null;
            });

            processedBarDatasets = page.barDatasets.map((barConfig) => ({
              label: barConfig.label || "",
              data: backgroundBarData, // sortedMonths-aligned ✓
              color: barConfig.color,
              order: barConfig.order || 0,
              hideInTable: true, // background bar table mein nahi dikhna chahiye
            }));
          }
        }

      }

      // ========== STEP 6: Process second table ==========
      let secondTableData = null;

      if (page?.showSecondTable && page?.secondTable) {
        const metric = page.secondTable.metric;
        const metricData = apiData[metric];

        console.log("Second Table Metric:", metric);
        console.log("Second Table Data:", metricData);

        if (metricData) {
          const rows = [];
          const sortedYears = Object.keys(yearMonthMap).sort();

          sortedYears.forEach((yr) => {
            const rowData = [];

            const yearMonths = Array.from(yearMonthMap[yr]).sort(
              (a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b)
            );

            yearMonths.forEach((mn) => {
              const value = metricData?.[yr]?.[mn];
              rowData.push(value ?? "-");
            });

            rows.push({ label: yr, data: rowData });
          });

          secondTableData = {
            title: page.secondTable.title,
            rows: rows,
          };

          console.log("Second Table Data Processed:", secondTableData);
        } else {
          console.warn(
            `Second table metric "${metric}" not found in apiData`
          );
        }
      }

      setChartData({
        months: monthLabels,
        processedDatasets: processedDatasets,
        processedBarDatasets: processedBarDatasets,
        yearRanges: yearRanges,
        secondTableData: secondTableData,
      });
    } catch (err) {
      console.error("Data fetch error:", err);
      setError(err.message || "Failed to load chart data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingComponent />;
  if (error) return <ErrorState error={error} onRetry={fetchData} />;

  return (
    <ChartTableComponent
      title={page?.title}
      subtitle={page?.subtitle}
      in_chart_content_line_1={page?.in_chart_content_line_1}
      datasets={chartData.processedDatasets}
      barDatasets={chartData.processedBarDatasets}
      showBars={chartData.processedBarDatasets.length > 0}
      months={chartData.months}
      yearRanges={chartData.yearRanges}
      yAxisTitle={page?.yAxisLabel}
      showRamadanRow={page?.showRamadan || true}
      // ramadanborderColor={page?.ramadanBorderColor || "#FFA500"}
      ramadanborderColor={"#E17A35"}
      showSecondTable={page?.showSecondTable || false}
      secondTableData={chartData.secondTableData}
        ramadanRevenueData={chartData.processedBarDatasets?.[0]?.data || []}
    />
  );
};

export default LineBars;